import { Trans } from '@lingui/macro'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { Box, Chip } from '@mui/material'
import React from 'react'

interface Filter {
  label: React.ReactNode
  value: string
}

interface FilterChipsProps {
  filters: Record<string, Filter[]>
  onRemoveFilter: (key: string, value: string) => void
  onClearAllFilters: () => void
}

/**
 * Reusable component for displaying active filters as removable chips.
 *
 * @component
 *
 * @param {FilterChipsProps} props - Component properties.
 * @param {Record<string, string[]>} props.filters - Object containing active filters, where keys are filter names and values are arrays of selected filter options.
 * @param {(key: string, value: string) => void} props.onRemoveFilter - Callback function triggered when a filter chip is removed.
 * @param {() => void} props.onClearAll - Callback function triggered when the "Clear All" chip is clicked.
 * @returns {JSX.Element} The rendered filter chips component.
 */
const FilterChips: React.FC<FilterChipsProps> = ({
  filters,
  onRemoveFilter,
  onClearAllFilters
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        margin: '14px 0px',
        padding: '0px 0px'
      }}
    >
      {Object.entries(filters).flatMap(([key, values]) =>
        values.map((filter) => {
          const { value, label } = filter
          return (
            <Chip
              key={`${key}-${value}`}
              label={label}
              onDelete={() => onRemoveFilter(key, value)}
              deleteIcon={<CloseOutlinedIcon />}
              sx={{
                border: '1px solid #EEEFE6',
                borderRadius: '0px',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '19px',
                letterSpacing: '-0.28px',
                color: '#000',
                backgroundColor: '#ffffff',
                '& .MuiChip-deleteIcon': {
                  color: '#17374F'
                }
              }}
            />
          )
        }
        )
      )}

      {Object.values(filters).some(value => value.length > 0) && (
        <Chip
          key='clearAll'
          label={<Trans>CLEAR_ALL</Trans>}
          onDelete={onClearAllFilters}
          deleteIcon={<CloseOutlinedIcon />}
          sx={{
            border: '1px solid #EEEFE6',
            borderRadius: '0px',
            color: '#000',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '19px',
            letterSpacing: '-0.28px',
            backgroundColor: '#ffffff',
            '& .MuiChip-deleteIcon': {
              color: '#17374F'
            }
          }}
        />
      )}
    </Box>
  )
}

export default FilterChips
